import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ClientDataStore } from '@app/Global/ClientDataStore';
import { GlobalFunctions } from '@app/Global/GlobalFunctions';
import { Subscription } from 'rxjs';
import { Documentation } from '@app/Components/Documentation/Documentation';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from '@app/Services/APIService';
import { ReleaseNote } from '@app/Global/Models/ClientModels';
import { StaticDataControllerMethods } from '@app/Global/EnumManager';
import { ReleaseNotes } from '@app/Components/ReleaseNotes/ReleaseNotes';
import { SecurityDataUploadComponent } from '../SecurityDataUpload/SecurityDataUpload';


@Component({
  selector: 'Home',
  templateUrl: './Home.html',
  styleUrls: ['./Home.scss']
})

export class HomeComponent implements OnInit {

  //For tracking if a server update request is in progress
  public IsGenerateInProgress = false;

  //Used for navigating to other pages
  public NavigationUrls;

  //Subscription needed to sync login status with observable, so that page elements can be shown/hidden as required, e.g. if not logged in, show the Login Button
  public MySubscription: Subscription;
  public LoginData = {
    LoginID: "", UserPassword: "", UserDisplayName: "", UserEmail: "", UserLoggedInStatus: false, UserMobile: "", AccessToken: ""
  }

  public AppName = this.globalFunctions.GetApplicationName();

  //An array of release notes
  public ReleaseNotes: ReleaseNote[] = [];
  //Flags to hide/show features on the home page
  public ShowPrivateNotes = false;
  public ShowPendingSettlements = false;
  public ShowSLAReporting = false;
  public ShowQCScoreReporting = false;
  public ShowApplicationSubmission = false;
  public ShowLIXISubmissions = false;
  public ShowAddReleaseNotesButton = false;
  public ShowReleaseNotes = false;
  public ShowSecurityDataUpload = false;

  //Unordered list needs a specific inline style to prevent bullet points from rendering outside the grid and into the margin.
  //Once release note data is migrated server side, we can use map properties and use template html on the client, and remove the in line styling here.
  private readonly UnorderedListHTML = '<ul class="glb_customFlexRow align-items-center" style="margin-left: 1em;text-align: left;">';

  constructor(private globalFunctions: GlobalFunctions, private clientDataStore: ClientDataStore, private router: Router, private dialog: MatDialog, private apiService: ApiService) {
    this.NavigationUrls = this.globalFunctions.navigationUrls;
  }

  ngOnInit(): void {

    this.globalFunctions.CheckBuildVersion();

    //Reset the login modal state on entry, so that it can be relaunched, if needed
    this.clientDataStore.LoginModalLaunched = false;

    //Reset error state when we hit the home page, so that we can query the server again
    this.clientDataStore.HitErrorPage = false;

    //Subscribe to LoginData so that we can hide the login button, and other elements, if needed
    this.MySubscription = this.clientDataStore.LoginData
      .subscribe(x => {
        if (!this.globalFunctions.isEmpty(x)) {
          this.LoginData = x;
        }
      })

    //Check if the user is logged in
    if (this.LoginData.UserLoggedInStatus) {

      //If they are, unlock the search bar input box.
      this.clientDataStore.Update_HideSearchBar(false);
    }

    //Testing an error
    //throw new RangeError();    

    //Check claims and decide what elements should be hidden or displayed
    //AMALEmployee claim - used to show the private/internal release notes
    if (this.globalFunctions.Claim_VerifyAMALEmployee(this.clientDataStore) === true) {
      this.ShowPrivateNotes = true;
    }

    //Pending Settlements
    if (this.globalFunctions.Claim_VerifyPermission(this.clientDataStore, "PendingSettlements", "Read") === true) {
      this.ShowPendingSettlements = true;
    }

    //SLA Reporting
    if (this.globalFunctions.Claim_VerifyPermission(this.clientDataStore, "SLAReporting", "Read") === true) {
      this.ShowSLAReporting = true;
    }

    //QC Feedback Score Reporting
    if (this.globalFunctions.Claim_VerifyPermission(this.clientDataStore, "ViewQualityCheckScoreReporting", "Read") === true) {
      this.ShowQCScoreReporting = true;
    }

    //Application Submissions
    if (this.globalFunctions.Claim_VerifyPermission(this.clientDataStore, "ApplicationSubmission", "Read") === true) {
      this.ShowApplicationSubmission = true;
    }

    //LIXI Submissions
    if (this.globalFunctions.Claim_VerifyPermission(this.clientDataStore, "ViewLIXISubmission", "Read") === true) {
      this.ShowLIXISubmissions = true;
    }

    //Add release notes
    if (this.globalFunctions.Claim_VerifyPermission(this.clientDataStore, "AddReleaseNotes", "Read") === true) {
      this.ShowAddReleaseNotesButton = true;
    }
  }

  //Launch Login modal
  public LoginModal_Launch(): void {
    this.globalFunctions.LaunchLoginModal(true, "Please log in", true);
  }
  //Launch the user guide
  public UserGuide_Launch(): void {
    this.globalFunctions.FeatureModal_Launch(Documentation, this.globalFunctions.GetFeatureModalConfig("95%"), this.dialog, "User Guide", 0, false, true);
  }

  //View Release Notes - loads and displays release notes
  public ViewReleaseNotes_Launch(): void {
    if (!this.ShowReleaseNotes) {

      // First time viewing - load the release notes
      this.ReleaseNotes_Get();
    }

    // Toggle the visibility
    this.ShowReleaseNotes = !this.ShowReleaseNotes;
  }

  //Navigate to a url, retaining state. Used for returning to pages
  public URLWithState_Navigate(link: string, incomingState = ''): void {
    this.router.navigate([link], { state: { Link: incomingState } });
  }

  //Add Release notes 
  public Launch_AddReleaseNotes() {
    //Display Client Selection modal first
    //Get the component of this modal, and set a property on it
    this.globalFunctions.FeatureModal_Launch(ReleaseNotes, this.globalFunctions.GetFeatureModalConfig('50%', false, false, "glb_fixedHeightDialog"), this.dialog, "Release Notes", "", false, false);
  }

  //Fetch release notes here
  private ReleaseNotes_Get(): void {

    // Clear any existing release notes before fetching new ones
    this.ReleaseNotes = [];

    // Track state of this request, so that we can show the spinner, if necessary.
    this.clientDataStore.SetShowFullscreenLoading(true);

    //Construct the request and send it to the server
    const apiRequest = {
    };

    this.apiService.APIData_Post(this.apiService.Endpoints.StaticDataController, StaticDataControllerMethods[StaticDataControllerMethods.GetReleaseNotes], apiRequest)
      .subscribe(apiResponse => {
        if (this.globalFunctions.isEmpty(apiResponse)) {

          // There was no response, or an error.
          this.clientDataStore.SetShowFullscreenLoading(false);
          return;
        }
        else {
          //Get the response, and try to display it in the document viewer
          const response = JSON.parse(JSON.stringify(apiResponse));

          if (!this.globalFunctions.isEmpty(response) && !this.globalFunctions.isEmpty(response.ReleaseNotes)) {
            response.ReleaseNotes.forEach((r: { GUID: string; CreatedDate: string; IsInternal: boolean; ReleaseNotes: string[]; LenderCodes: string; ReleaseDate: string }) => {
              this.ReleaseNotes.push({
                GUID: r.GUID,
                CreatedDate: r.CreatedDate,
                IsInternal: r.IsInternal,
                ReleaseNoteDetails: r.ReleaseNotes,
                LenderCodes: r.LenderCodes,
                ReleaseDate: this.globalFunctions.isEmpty(r.ReleaseDate) ? "" : this.globalFunctions.getCustomDateFormat(r.ReleaseDate, "shortdate", "custom", "YYYY/MM/DD"),
              });
            });
          }

          // Turn off the fullscreen loading
          this.clientDataStore.SetShowFullscreenLoading(false);

          // Show the release notes section after loading
          this.ShowReleaseNotes = true;
          return;
        }
      });
  }
}